import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Typography from '@material-ui/core/Typography';
import { Button, Card, Link, TextField } from "@material-ui/core";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { Autocomplete } from "@material-ui/lab";
import CloseIcon from '@material-ui/icons/Close';
import ImageIcon from '@material-ui/icons/Image';
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { createOrderAction, fetchCustomerAddressAction, getCategoriesAction, getCustomerAction, getItemPriceAction, getItemsAction, getOrderDetailsAction, getOrderFormAction, getSalesPersonAction, getVeneerCategoriesAction, updateOrderAction } from "../../store/new-order/asyncActions";
import { useSelector } from "react-redux";
import Joi from "joi";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CreatableSelect from 'react-select/creatable';
import { BillingShippingAddress } from "./BillingShippingAddress";

const NewOrder = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userData = localStorage.getItem('user');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');

  useEffect(() => {
    document.title = 'New Order - Natural Veneers';
  }, []);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redir, setRedir] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  useEffect(() => {
    if (userData && userData.trim() !== '') {
      setUser(JSON.parse(userData));
    }
  }, [userData]);

  useEffect(() => {
    if (user && id && id.trim() !== '') {
      dispatch(getOrderDetailsAction({
        auth_token: user?.auth_token,
        user_type: user?.user_type,
        user_id: user?.user_id,
        order_id: id
      }))
    }
  }, [user, id]);

  useEffect(() => {
    if (user) {
      const isCustomer = user?.user_type === "Customer";
      setOrderForm((prevOrderForm) => ({
        ...prevOrderForm,
        auth_token: user?.auth_token,
        user_type: user?.user_type,
        user_id: user?.user_id,
        ...(isCustomer && { customer_id: user?.user_id }),
      }));
      if (!isCustomer) {
        dispatch(getSalesPersonAction({
          auth_token: user?.auth_token,
          user_type: user?.user_type,
          user_id: user?.user_id,
        }));
      }
      dispatch(getCategoriesAction({
        auth_token: user?.auth_token,
        user_type: user?.user_type,
        user_id: user?.user_id,
      }));
      
      // Auto-fetch customer addresses when user is Customer and it's a new order
      if (isCustomer && user?.user_id && !id) {
        dispatch(fetchCustomerAddressAction({
          auth_token: user?.auth_token,
          customer_id: user?.user_id
        }));
      }
    }
  }, [user, id, dispatch]);

  const salesPersonsData = useSelector((state) => state?.newOrder?.salesPersons);
  const customersData = useSelector((state) => state?.newOrder?.customers);
  const categoryData = useSelector((state) => state?.newOrder?.categories);
  const veneerCategoryData = useSelector((state) => state?.newOrder?.veneerCategories);
  const itemsData = useSelector((state) => state?.newOrder?.items);
  const formFieldData = useSelector((state) => state?.newOrder?.orderForm);
  const createOrderData = useSelector((state) => state?.newOrder?.createOrder);
  const orderDetailsData = useSelector((state) => state?.newOrder?.orderDetails);
  const updateOrderData = useSelector((state) => state?.newOrder?.updateOrder);
  const itemPriceData = useSelector((state) => state?.newOrder?.itemPrice);
  const fetchCustomerAddressData = useSelector((state) => state?.newOrder?.fetchCustomerAddress);

  const [orderItems, setOrderItems] = useState([{
    category: undefined,
    item_id: undefined,
    qty: undefined,
    rate: undefined,
    comments: undefined
    }]);

  const updateOrderItem = (index, field, value) => {
    const updatedOrderItems = [...orderItems];
    if (field === 'category') {
      // When category changes, reset all dynamic form fields for THIS SPECIFIC ITEM ONLY
      const currentItem = updatedOrderItems[index] || {};
      
      if (value) {
        // Create a new object with only base fields (reset all dynamic fields)
        const resetItem = {
          category: value,
          item_id: undefined,
          qty: currentItem.qty,
          rate: currentItem.rate,
          comments: currentItem.comments,
          ...((value !== '4' && value !== '8' && value !== '14' && value !== '42' && value !== '43') && { veneer_category_id: undefined, veneer_item_id: undefined })
        };
        updatedOrderItems[index] = resetItem;
      } else {
        // Create a new object with only base fields (reset all dynamic fields)
        const resetItem = {
          category: undefined,
          item_id: undefined,
          qty: currentItem.qty,
          rate: currentItem.rate,
          comments: currentItem.comments,
          veneer_category_id: undefined,
          veneer_item_id: undefined
        };
        updatedOrderItems[index] = resetItem;
      }
      // Clear form fields for THIS SPECIFIC INDEX ONLY, not all items
      const updatedFormFields = [...formFields]; // Create a shallow copy of the entire array
      updatedFormFields[index] = null; // Only clear form fields for this index
      setFormFields(updatedFormFields);
    } else if (field === 'item_id') {
      // When item_id changes, reset all dynamic form fields for THIS SPECIFIC ITEM ONLY
      // Keep only base fields: category, item_id, qty, rate, comments
      const currentItem = updatedOrderItems[index] || {};
      
      // Get the current formFields for this index to identify which dynamic fields to reset
      const currentFormFieldsForIndex = formFields?.[index] || [];
      
      // Create a new object with only base fields (reset all dynamic fields including veneer fields)
      // This only affects the item at the specific index, not other items
      const resetItem = {
        category: currentItem.category,
        item_id: value, // new item_id
        qty: currentItem.qty,
        rate: currentItem.rate,
        comments: currentItem.comments
      };
      
      // Only update the item at this specific index
      updatedOrderItems[index] = resetItem;
      
      // Clear form fields for THIS SPECIFIC INDEX ONLY before fetching new ones
      // Create a proper copy of the formFields array to preserve other items' form fields
      const updatedFormFields = [...formFields]; // Create a shallow copy of the entire array
      // Only modify the formFields for this specific index, leave all other indices unchanged
      updatedFormFields[index] = null;
      setFormFields(updatedFormFields);
    } else if (field === 'veneer_category_id') {
      if (value) {
        updatedOrderItems[index] = {
          ...updatedOrderItems[index],
          [field]: value,
          veneer_item_id: undefined,
        };
      } else {
        updatedOrderItems[index] = {
          ...updatedOrderItems[index],
          [field]: undefined,
          veneer_item_id: undefined,
        };
      }
    } else {
      updatedOrderItems[index] = {
        ...updatedOrderItems[index],
        [field]: value
      };
    }
    setOrderItems(updatedOrderItems);
  }

  const [orderForm, setOrderForm] = useState(null);

  const [salesPerson, setSalesPerson] = useState(null);
  const [selecedSalesPerson, setSelecedSalesPerson] = useState(null);

  const [customers, setCustomers] = useState(null);
  const [selecedCustomers, setSelecedCustomers] = useState(null);

  const [categories, setCategories] = useState(null);
  const [selecedCategory, setSelecedCategory] = useState([]);

  const [veneerCategories, setVeneerCategories] = useState(null);
  const [selecedVeneerCategory, setSelecedVeneerCategory] = useState([]);

  const [items, setItems] = useState([]);
  const [selecedItem, setSelecedItem] = useState([]);

  const [itemsIndex, setItemsIndex] = useState(null);
  const [formFields, setFormFields] = useState([]);

  const [orderDetails, setOrderDetails] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState(null);

  useEffect(() => {
    if (orderItems) {
      setOrderForm({
        ...orderForm,
        items: orderItems
      });
    }
  }, [orderItems]);

  useEffect(() => {
    if (salesPersonsData?.data?.success) {
      setSalesPerson(salesPersonsData?.data?.data);
    }
  }, [salesPersonsData]);

  useEffect(() => {
    if (customersData?.data?.success) {
      setCustomers(customersData?.data?.data);
    }
  }, [customersData]);

  useEffect(() => {
    if (categoryData?.data?.success) {
      setCategories(categoryData?.data?.data);
    }
  }, [categoryData]);

  useEffect(() => {
    if (veneerCategoryData?.data?.success) {
      setVeneerCategories(veneerCategoryData?.data?.data);
    }
  }, [veneerCategoryData]);

  useEffect(() => {
    if (itemsData?.data?.success) {
      addItemAtIndex(itemsIndex, itemsData?.data?.data);
    } else if (itemsData?.data?.error) {
      addItemAtIndex(itemsIndex, null);
    }
  }, [itemsData]);

  useEffect(() => {
    if (formFieldData?.data?.success) {
      addFormFieldAtIndex(itemsIndex, formFieldData?.data?.data);
    } else if (formFieldData?.data?.error) {
      addFormFieldAtIndex(itemsIndex, null);
    }
  }, [formFieldData]);

  useEffect(() => {
    if (createOrderData?.data?.success) {
      setLoading(false);
      createOrderData.data = null;
      if (redir && redir === 'New') {
        window.location.reload();
      } else if (redir && redir === 'Exit') {
        history.push('/orders');
      }
    } else if (createOrderData?.data?.error) {
      setLoading(false);
      createOrderData.data = null;
    }
  }, [createOrderData]);

  useEffect(() => {
    if (orderDetailsData?.data?.success) {
      setOrderDetails(orderDetailsData?.data?.data);
      orderDetailsData.data = null;
    }
  }, [orderDetailsData]);

  useEffect(() => {
    if (updateOrderData?.data?.success) {
      setLoading(false);
      updateOrderData.data = null;
      if (redir && redir === 'New') {
        window.location.href = '/new-order';
      } else if (redir && redir === 'Exit') {
        history.push('/orders');
      }
    } else if (updateOrderData?.data?.error) {
      setLoading(false);
      updateOrderData.data = null;
    }
  }, [updateOrderData]);

  useEffect(() => {
    const indexToUpdate = itemsIndex;

    if (indexToUpdate === null) return;

    const updatedOrderItems = [...orderItems];

    if (itemPriceData?.data?.success) {
      const { rate } = itemPriceData.data.data;
      updatedOrderItems[indexToUpdate].rate = rate || '';
    } else {
      updatedOrderItems[indexToUpdate].rate = '';
    }

    setOrderItems(updatedOrderItems);
  }, [itemPriceData]);

  useEffect(() => {
    if (fetchCustomerAddressData?.data?.success) {
      const addressData = fetchCustomerAddressData.data.data;
      setCustomerAddresses(addressData);
      
      // Transform and set addresses in order form
      if (addressData?.addresses) {
        const billing = addressData.addresses.billing;
        const shipping = addressData.addresses.shipping;
        
        if (billing && shipping) {
          const transformedAddress = {
            mobile_no: billing.mobile_no || '',
            billing_name: billing.name || '',
            billing_addressline_1: billing.addressline_1 || '',
            billing_addressline_2: billing.addressline_2 || '',
            billing_gst_no: billing.gst_no || '',
            billing_country_id: billing.country_id?.country_id || '',
            billing_state_id: billing.state_id?.state_id || '',
            billing_city: billing.city?.city_id || '',
            billing_pincode: billing.pincode || '',
            delivery_name: shipping.name || '',
            contact_person: shipping.contact_person || '',
            contact_number: shipping.contact_number || '',
            delivery_addressline_1: shipping.addressline_1 || '',
            delivery_addressline_2: shipping.addressline_2 || '',
            delivery_country_id: shipping.country_id?.country_id || '',
            delivery_state_id: shipping.state_id?.state_id || '',
            delivery_city: shipping.city?.city_id || '',
            delivery_pincode: shipping.pincode || '',
          };
          
          setOrderForm({
            ...orderForm,
            addresses: transformedAddress
          });
        }
      }
      fetchCustomerAddressData.data = null; // Reset after use
    }
  }, [fetchCustomerAddressData]);

  useEffect(() => {
    if (orderDetails) {
      setOrderItems(orderDetails?.items.map((itm) => {
        return {
          ...itm,
          item_id: itm?.item?.item_id
        }
      }));
      setOrderForm({
        ...orderForm,
        auth_token: user?.auth_token,
        user_type: user?.user_type,
        user_id: user?.user_id,
        order_id: id,
        sales_person_id: orderDetails?.sales_person_id,
        customer_id: orderDetails?.customer_id,
        items: orderItems,
      });
      if (orderDetails?.items && categories) {
        const eidtCategories = [];
        const eidtItems = [];
        const eidtFormField = [];

        const eidtVeneerCategories = [];
        // const eidtVeneerItems = [];

        orderDetails?.items.map((item) => {
          eidtCategories.push(categories.filter(x => x.id === item?.category)?.[0]);
          eidtItems.push({ id: item?.item?.item_id, name: item?.item?.item_name });
          eidtFormField.push(item?.data);
          eidtVeneerCategories.push({ id: item?.veneer_category?.id, name: item?.veneer_category?.name });
          // eidtVeneerItems.push({ id: item?.item?.item_id, name: item?.item?.item_name });
        });
        setSelecedCategory(eidtCategories);
        setSelecedItem(eidtItems);
        setFormFields(eidtFormField);
        setSelecedVeneerCategory(eidtVeneerCategories);
      }
    }

    if (orderDetails && salesPerson) {
      setSelecedSalesPerson(salesPerson.filter(x => x?.id === orderDetails?.sales_person_id)?.[0]);
    }
    if (orderDetails?.customer_id && orderDetails?.customer_name) {
      setSelecedCustomers({ id: orderDetails?.customer_id, name: orderDetails?.customer_name })
    }

  }, [orderDetails, salesPerson, categories]);

  const addItem = () => {
    setOrderItems([...orderItems, {
      category: undefined,
      item_id: undefined,
      qty: undefined,
      rate: undefined,
      comments: undefined
    }]);
  };

  const addSelectedCategoryAtIndex = (index, selectedOptions) => {
    const newArray = [...selecedCategory];
    newArray[index] = selectedOptions;
    setSelecedCategory(newArray);
  };

  const addSelectedVeneerCategoryAtIndex = (index, selectedOptions) => {
    const newArray = [...selecedVeneerCategory];
    newArray[index] = selectedOptions;
    setSelecedVeneerCategory(newArray);
  };

  const addItemAtIndex = (index, data) => {
    const newArray = [...items];
    newArray[index] = data;
    setItems(newArray);
  };

  const addSelectedItemAtIndex = (index, selectedOptions) => {
    const newArray = [...selecedItem];
    newArray[index] = selectedOptions;
    setSelecedItem(newArray);
  };

  const addFormFieldAtIndex = (index, data) => {
    const newArray = [...formFields];
    newArray[index] = data;
    setFormFields(newArray);
  };

  const deleteItem = (index) => {
    const updateOrderdItems = [...orderItems];
    updateOrderdItems.splice(index, 1);
    setOrderItems(updateOrderdItems);

    const updatedSelectedCategory = [...selecedCategory];
    updatedSelectedCategory.splice(index, 1);
    setSelecedCategory(updatedSelectedCategory);

    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);

    const updatedSelectedItems = [...selecedItem];
    updatedSelectedItems.splice(index, 1);
    setSelecedItem(updatedSelectedItems);

    const updatedFormField = [...formFields];
    updatedFormField.splice(index, 1);
    setFormFields(updatedFormField);
  };

  const findOptionObject = (options, id) => {
    const result = options.filter(x => id === x.id);
    return result[0];
  }

  const findOptionMultipleObject = (options, arr) => {
    if (!options || !arr) {
      return [];
    }
    const result = options.filter(item => arr.includes(item.id));
    return result;
  }

  const [errors, setErrors] = useState({});
  const getSchema = (userType) => {
    const isCustomer = userType === "Customer";
    return Joi.object({
      auth_token: Joi.string().required(),
      user_type: Joi.string().required(),
      user_id: Joi.string().required(),
      sales_person_id: isCustomer ? Joi.allow() : Joi.string().required().label('Sales Person'),
      customer_id: Joi.string().required().label('Party Name'),
      fright_charges: isCustomer ? Joi.allow('', null) : Joi.number().allow('', null).label('Freight Charge'),
      order_id: Joi.allow(),
      items: Joi.array().items(
        Joi.object({
          category: Joi.string().required().label('Category'),
          item_id: Joi.string().required().label('Item'),
          qty: Joi.string().required().label('Qty'),
          rate: Joi.string().required().label('Rate'),
          comments: Joi.string().allow(null, ''),
        }).unknown(true)
      ).required(),
      addresses: Joi.object().allow()
    });
  };

  const save_order = (str) => {
    const schema = getSchema(user?.user_type);
    const validationResult = schema.validate(orderForm, { abortEarly: false });

    if (validationResult.error) {
      const validationErrors = {};
      validationResult.error.details.forEach((err) => {
        if (err.path[0] === 'items') {
          validationErrors[err.path[0]] = { ...validationErrors[err.path[0]], [err.path[1]]: { ...validationErrors?.[err.path[0]]?.[err.path[1]], [err.path[2]]: err.message } };
        } else {
          validationErrors[err.path[0]] = err.message;
        }
      });
      setErrors(validationErrors);
    } else {
      setErrors({});
      setRedir(str);
      setLoading(true);
      if (id && id.trim() !== '') {
        dispatch(updateOrderAction(orderForm));
      } else {
        dispatch(createOrderAction(orderForm));
      }
    }

    // setRedir(str);
    // setLoading(true);
    // if (id && id.trim() !== '') {
    //   dispatch(updateOrderAction(orderForm));
    // } else {
    //   dispatch(createOrderAction(orderForm));
    // }
  }

  return (
    <>
      <div className="px-1 pt-1 pb-3">
        <div className="title_With_Back mb-1">
          <Typography variant='h6' color="textSecondary">
            {id ? 'Edit ORDER' : 'ADD NEW ORDER'}
          </Typography>
          <Button variant="text" color="primary" onClick={() => {
            history.goBack();
          }}>
            <ArrowBackIosIcon />Back
          </Button>
        </div>
        <div>
          {user?.user_type !== "Customer" && (
            <CustomSelect
              className="mb-1"
              title={'Select Sales Person'}
              options={salesPerson || []}
              labelKey={'name'}
              value={selecedSalesPerson}
              error={errors?.sales_person_id}
              helperText={errors?.sales_person_id}
              onSelect={(selectedOptions) => {
                setSelecedSalesPerson(selectedOptions);
                setOrderForm({
                  ...orderForm,
                  sales_person_id: selectedOptions?.id
                });
                document.activeElement.blur();
              }}
            />
          )}
          {user?.user_type !== "Customer" && (
            <CustomSelect
              className="mb-1"
              title={'Select Party Name'}
              options={customers || []}
              labelKey={'name'}
              value={selecedCustomers}
              error={errors?.customer_id}
              helperText={errors?.customer_id}
              disabled={id && id.trim() !== ''}
              onSearch={(newInputValue) => {
                if (newInputValue && newInputValue.trim() !== '') {
                  dispatch(getCustomerAction({
                    auth_token: user?.auth_token,
                    user_type: user?.user_type,
                    user_id: user?.user_id,
                    search: newInputValue
                  }));
                }
              }}
              onSelect={(selectedOptions) => {
                setSelecedCustomers(selectedOptions);
                setOrderForm({
                  ...orderForm,
                  customer_id: selectedOptions?.id
                });
                
                // Call fetchCustomerAddress API when customer is selected
                if (selectedOptions?.id && user && !id) {
                  dispatch(fetchCustomerAddressAction({
                    auth_token: user?.auth_token,
                    customer_id: selectedOptions?.id
                  }));
                }
                
                document.activeElement.blur();
              }}
            />
          )}
          {user?.user_type !== "Customer" && (
            <TextField
              className="login_form_field mb-1"
              name="fright_charges"
              variant="outlined"
              size="small"
              placeholder="Freight Charge"
              fullWidth
              type="number"
              error={errors?.fright_charges}
              helperText={errors?.fright_charges}
              value={orderForm?.fright_charges || ''}
              onChange={(e) => {
                setOrderForm({
                  ...orderForm,
                  fright_charges: e?.target?.value
                });
              }}
            />
          )}
        </div>
        {/* {id && */}
          <div>
            <Link component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                setAddressDialogOpen(true);
              }}>
              Billing & Shipping Address
            </Link>
          </div>
        {/* } */}
        <div className="mt-1">
          <Typography variant='h6' color="textSecondary" className="oreder_summary_title">
            ORDER SUMMARY
          </Typography>
        </div>
        <div>

          {orderItems && orderItems.map((x, index) => {
            return (
              <Card className="py-1 px-1 my-1" key={index}>
                <div className="order_item_title">
                  <Typography variant='subtitle2' color="inherit">
                    Item 0{index + 1}
                  </Typography>
                  <CloseIcon className="item_delete_btn" onClick={() => deleteItem(index)} />
                </div>
                <div className="mt-1">
                  <CustomSelect
                    className="mb-1"
                    title={'Select Category'}
                    options={categories || []}
                    labelKey={'name'}
                    value={selecedCategory?.[index] || null}
                    error={errors?.items?.[index]?.category}
                    helperText={errors?.items?.[index]?.category}
                    onSelect={(selectedOptions) => {
                      if (selectedOptions) {
                        updateOrderItem(index, 'category', selectedOptions?.id);
                      } else {
                        updateOrderItem(index, 'category', undefined);
                      }

                      addSelectedCategoryAtIndex(index, selectedOptions);
                      setItemsIndex(index);
                      addSelectedItemAtIndex(index, null);
                      dispatch(getItemsAction({
                        auth_token: user?.auth_token,
                        user_type: user?.user_type,
                        user_id: user?.user_id,
                        category_id: selectedOptions?.id
                      }));
                      if (selectedOptions?.id === '4' || selectedOptions?.id === '8' || selectedOptions?.id === '14' || selectedOptions?.id === '42' || selectedOptions?.id === '43') {
                        dispatch(getVeneerCategoriesAction({
                          auth_token: user?.auth_token,
                          user_type: user?.user_type,
                          user_id: user?.user_id,
                          category_id: selectedOptions?.id
                        }));
                      } else {
                        setSelecedVeneerCategory([]);
                      }
                      document.activeElement.blur();
                    }}
                  />
                  <CustomSelect
                    className="mb-1"
                    title={'Select Item / Design'}
                    options={items?.[index] || []}
                    labelKey={'name'}
                    value={selecedItem?.[index] || null}
                    error={errors?.items?.[index]?.item_id}
                    helperText={errors?.items?.[index]?.item_id}
                    onSearch={(newInputValue) => {
                      setItemsIndex(index);
                      dispatch(getItemsAction({
                        auth_token: user?.auth_token,
                        user_type: user?.user_type,
                        user_id: user?.user_id,
                        category_id: selecedCategory?.[index]?.['id'],
                        search: newInputValue
                      }));
                    }}
                    onSelect={(selectedOptions) => {
                      addSelectedItemAtIndex(index, selectedOptions);
                      // Reset veneer category selection when item changes
                      addSelectedVeneerCategoryAtIndex(index, null);
                      updateOrderItem(index, 'item_id', selectedOptions?.id);
                      dispatch(getOrderFormAction({
                        auth_token: user?.auth_token,
                        user_type: user?.user_type,
                        user_id: user?.user_id,
                        category_id: selecedCategory?.[index]?.['id'],
                        item_id: selectedOptions?.id
                      }));
                      document.activeElement.blur();
                    }}
                  />

                  {(x?.category === '4' || x?.category === '8' || x?.category === '14' || x?.category === '42' || x?.category === '43') &&
                    <CustomSelect
                      className="mb-1"
                      title={x?.category === '42' || x?.category === '43' ? 'Select Design Category' : 'Select Species'}
                      options={veneerCategories || []}
                      labelKey={'name'}
                      value={selecedVeneerCategory?.[index] || null}
                      error={errors?.items?.[index]?.veneer_category_id}
                      helperText={errors?.items?.[index]?.veneer_category_id}
                      onSearch={(newInputValue) => {
                        setItemsIndex(index);
                        dispatch(getVeneerCategoriesAction({
                          auth_token: user?.auth_token,
                          user_type: user?.user_type,
                          user_id: user?.user_id,
                          category_id: selecedCategory?.[index]?.['id'],
                          search: newInputValue
                        }));
                      }}
                      onSelect={(selectedOptions) => {
                        if (selectedOptions) {
                          addSelectedVeneerCategoryAtIndex(index, selectedOptions);
                          updateOrderItem(index, 'veneer_category_id', selectedOptions?.id);
                          dispatch(getOrderFormAction({
                            auth_token: user?.auth_token,
                            user_type: user?.user_type,
                            user_id: user?.user_id,
                            category_id: selecedCategory?.[index]?.['id'],
                            veneer_category_id: selectedOptions?.id
                          }));
                        } else {
                          setSelecedVeneerCategory([]);
                          updateOrderItem(index, 'veneer_category_id', undefined);
                        }
                        document.activeElement.blur();
                      }}
                    />
                  }

                  {formFields && formFields?.[index] && formFields?.[index].map((itm, i) => {
                    if (itm?.type === 'dropdown') {
                      return (
                        <CustomSelect
                          key={`${index}-${i}-${itm?.id}`}
                          className="mb-1"
                          title={itm?.name}
                          options={itm?.options || []}
                          labelKey={'name'}
                          value={findOptionObject(itm?.options, orderItems?.[index]?.[itm?.id]) || null}
                          onSelect={(selectedOptions) => {
                            updateOrderItem(index, itm?.id, selectedOptions?.id);
                            document.activeElement.blur();
                            if (itm?.name === 'Select Size') {
                              dispatch(getItemPriceAction({
                                auth_token: user?.auth_token,
                                user_type: user?.user_type,
                                user_id: user?.user_id,
                                ...orderItems?.[index],
                                size_id: selectedOptions?.id
                              }));
                            }
                          }}
                        />
                      )
                    }
                    if (itm?.type === 'multidropdown') {
                      return (
                        <CustomSelect
                          key={`${index}-${i}-${itm?.id}-multi`}
                          className="mb-1"
                          multiple={true}
                          title={itm?.name}
                          options={itm?.options || []}
                          labelKey={'name'}
                          value={findOptionMultipleObject(itm?.options, orderItems?.[index]?.[itm?.id]) || []}
                          onSelect={(selectedOptions) => {
                            updateOrderItem(index, itm?.id, selectedOptions.map(x => x?.id));
                            document.activeElement.blur();
                          }}
                        />
                      )
                    }
                    if (itm?.type === 'dropdown-creatable') {
                      return (
                        <CreatableSelect
                          key={`${index}-${i}-${itm?.id}-creatable`}
                          name={itm?.id}
                          isClearable
                          options={itm?.options}
                          value={orderItems?.[index]?.[itm?.id] || null}
                          onChange={(newValue) => {
                            updateOrderItem(index, itm?.id, newValue);
                          }}
                          placeholder={itm?.name}
                          styles={customStyles}
                          className="mb-1"
                        />
                      )
                    }
                    if (itm?.type === 'input') {
                      return (
                        <TextField
                          key={`${index}-${i}-${itm?.id}-input`}
                          className="login_form_field mb-1"
                          name={itm?.id}
                          variant="outlined"
                          size="small"
                          placeholder={itm?.name}
                          fullWidth
                          value={orderItems?.[index]?.[itm?.id] || ''}
                          onChange={(e) => {
                            updateOrderItem(index, itm?.id, e?.target?.value);
                          }}
                        />
                      )
                    }
                    if (itm?.type === 'file') {
                      if (orderItems?.[index]?.[itm?.id] && orderItems?.[index]?.[itm?.id].trim() !== '') {
                        return (
                          <div className='fileDragArea mb-1' key={`${index}-${i}-${itm?.id}-file`}>
                            <div className='imgPrevOuter'>
                              <img src={orderItems?.[index]?.[itm?.id]} alt="Image Preview" />
                              <div className='imgRemove' onClick={() => {
                                updateOrderItem(index, itm?.id, null);
                              }}>
                                <CloseIcon />
                              </div>
                            </div>
                          </div>
                        )
                      } else {
                        return (
                          <div className='fileDragArea mb-1' key={`${index}-${i}-${itm?.id}-file-empty`}>
                            <input type="file" accept="image/*" onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  updateOrderItem(index, itm?.id, reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }} />
                            <div className='iconDiv'><ImageIcon /></div>
                            <div className='fileupTxt'>{itm?.name}</div>
                            <div style={{ paddingTop: '10px' }}>
                              <Button color="primary" variant='contained'>
                                Browse file
                              </Button>
                            </div>
                          </div>
                        )
                      }
                    }
                  })}

                  <TextField
                    className="login_form_field mb-1"
                    name="qty"
                    variant="outlined"
                    size="small"
                    placeholder="Qty"
                    fullWidth
                    error={errors?.items?.[index]?.qty}
                    helperText={errors?.items?.[index]?.qty}
                    value={orderItems?.[index]?.['qty'] || ''}
                    onChange={(e) => {
                      updateOrderItem(index, 'qty', e?.target?.value);
                    }}
                  />
                  <TextField
                    className="login_form_field mb-1"
                    name="rate"
                    variant="outlined"
                    size="small"
                    placeholder="Rate"
                    fullWidth
                    error={errors?.items?.[index]?.rate}
                    helperText={errors?.items?.[index]?.rate}
                    value={orderItems?.[index]?.['rate'] || ''}
                    onChange={(e) => {
                      updateOrderItem(index, 'rate', e?.target?.value);
                    }}
                  />
                  <TextField
                    className="login_form_field"
                    name="comments"
                    variant="outlined"
                    multiline
                    rows={4}
                    placeholder="Comments..."
                    fullWidth
                    value={orderItems?.[index]?.['comments'] || ''}
                    onChange={(e) => {
                      updateOrderItem(index, 'comments', e?.target?.value);
                    }}
                  />
                </div>
              </Card>
            )
          })}

          <div className="mb-2">
            <Button variant="outlined" color="primary" fullWidth onClick={() => addItem()}>
              + ADD NEW
            </Button>
          </div>
        </div>
      </div>

      <div className="new_order_footer">
        <Button variant="contained" color="default" fullWidth disabled={loading} onClick={() => save_order('New')}>
          {id ? 'UPDATE & ADD NEW' : 'SAVE & ADD NEW'}
        </Button>
        <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={() => save_order('Exit')}>
          {id ? 'UPDATE & EXIT' : 'SAVE & EXIT'}
        </Button>
      </div>

      <BillingShippingAddress
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        user={{
          auth_token: user?.auth_token,
          user_type: user?.user_type,
          user_id: user?.user_id,
        }}
        order_id={id}
        customerAddresses={customerAddresses}
        currentAddresses={orderForm?.addresses}
        onAddressChange={(addresses) => {
          setOrderForm({
            ...orderForm,
            addresses: addresses
          });
        }}
      />

    </>
  );
};

export default NewOrder;

const baseFont = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: "0.875rem",
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    ...baseFont,
    borderColor: state.isFocused ? "#23b14d" : provided.borderColor,
    boxShadow: state.isFocused ? "0 0 0 1px #23b14d" : provided.boxShadow,
    "&:hover": {
      borderColor: state.isFocused ? "0 0 0 1px #23b14d" : "#000000ff",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    ...baseFont,
    backgroundColor: state.isSelected
      ? "#23b14d"
      : state.isFocused
        ? "#d6f5e3"
        : "transparent",
    color: state.isSelected ? "white" : "black",
    "&:hover": {
      backgroundColor: "#d6f5e3",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    ...baseFont,
  }),
  placeholder: (provided) => ({
    ...provided,
    ...baseFont,
  }),
  input: (provided) => ({
    ...provided,
    ...baseFont,
  }),
  multiValue: (provided) => ({
    ...provided,
    ...baseFont,
    backgroundColor: "#23b14d",
    color: "white",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    ...baseFont,
    color: "white",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    ...baseFont,
    color: "white",
    "&:hover": {
      backgroundColor: "#1d943f",
      color: "white",
    },
  }),
};
